import * as React from 'react'
import { cn } from '../../lib/cn'
import { StageEmptyState } from './StageEmptyState'
import type { LaneData } from './types'

export interface LaneCardProps extends React.HTMLAttributes<HTMLDivElement> {
  lane: LaneData
}

/** Min height of an empty lane card so the centered prompt has room. */
const EMPTY_LANE_MIN_H = 248

const DEFAULT_EMPTY_HINT = {
  title: 'Nothing here yet',
  description: 'Add an action to run on this branch.',
}

/**
 * Narrow stage-styled card used inside `BranchedLaneRow`. The accent
 * color is set via the `--stage-accent` CSS custom property, which
 * drives the top-border line and the gradient on the lane body.
 *
 * Width fixed at 280px per mockup.
 *
 * Source of truth: `mockups/unified-pipeline.html` `.lane`.
 */
export const LaneCard = React.forwardRef<HTMLDivElement, LaneCardProps>(function LaneCard(
  { className, lane, style, ...rest },
  ref,
) {
  const isEmpty = React.Children.count(lane.children) === 0

  const composedStyle: React.CSSProperties = {
    width: 280,
    flexShrink: 0,
    ['--stage-accent' as string]: lane.accentColor,
    background: lane.backgroundTint
      ? `linear-gradient(180deg, ${lane.backgroundTint} 0%, var(--pe-bg-card) 30%)`
      : 'var(--pe-bg-card)',
    borderColor: 'var(--pe-border-card)',
    // Empty lanes stay compact + vertically centered (alignSelf overrides
    // the branch row's stretch) so the fork connector meets a tidy card
    // rather than a full-height void with a lonely Add button.
    ...(isEmpty ? { alignSelf: 'center', minHeight: EMPTY_LANE_MIN_H } : null),
    ...style,
  }

  return (
    <div
      ref={ref}
      className={cn(
        'relative flex flex-col overflow-hidden rounded-[10px] border transition-colors',
        'hover:[border-color:var(--pe-border-strong)]',
        className,
      )}
      style={composedStyle}
      data-testid="pipeline-lane-card"
      data-lane-id={lane.id}
      {...rest}
    >
      {/* Top accent line */}
      <span
        aria-hidden
        className="absolute left-0 right-0 top-0"
        style={{
          height: 2,
          background: 'var(--stage-accent)',
          opacity: 0.6,
        }}
      />

      <div
        className="px-4 pt-3.5 pb-3 border-b shrink-0"
        style={{
          borderColor: 'var(--pe-border-subtle)',
          backgroundImage: 'linear-gradient(180deg, var(--pe-bg-card-elev) 0%, transparent 100%)',
        }}
      >
        <div className="flex items-center gap-2.5 mb-1">
          <span
            className="rounded border px-1.5 py-0.5 font-mono text-[10px]"
            style={{
              letterSpacing: '0.10em',
              color: 'var(--pe-text-muted)',
              background: 'color-mix(in srgb, var(--pe-text-primary) 3%, transparent)',
              borderColor: 'var(--pe-border-subtle)',
            }}
          >
            {lane.num}
          </span>
          <span
            className="font-mono text-[12px] font-semibold uppercase"
            style={{ letterSpacing: '0.12em', color: 'var(--stage-accent)' }}
          >
            {lane.label}
          </span>
          {lane.counter !== undefined && (
            <span
              className="ml-auto rounded-full border px-2 py-0.5 font-mono text-[10px]"
              style={{
                color: 'var(--pe-text-muted)',
                background: 'color-mix(in srgb, var(--pe-text-primary) 3%, transparent)',
                borderColor: 'var(--pe-border-subtle)',
              }}
            >
              {lane.counter}
            </span>
          )}
        </div>
        {lane.purpose && (
          <p className="text-[11px] leading-[1.4]" style={{ color: 'var(--pe-text-tertiary)' }}>
            {lane.purpose}
          </p>
        )}
      </div>

      {isEmpty ? (
        <div
          className="flex-1 flex flex-col items-center justify-center p-4"
          data-testid="pipeline-lane-body"
        >
          <StageEmptyState
            hint={lane.emptyHint ?? DEFAULT_EMPTY_HINT}
            accent={lane.accentColor}
            action={lane.footer}
          />
        </div>
      ) : (
        <>
          <div
            className="flex-1 overflow-y-auto p-3 flex flex-col gap-1.5"
            data-testid="pipeline-lane-body"
          >
            {lane.children}
          </div>

          {lane.footer && (
            <div
              className="px-3 py-2.5 border-t shrink-0"
              style={{ borderColor: 'var(--pe-border-subtle)' }}
            >
              {lane.footer}
            </div>
          )}
        </>
      )}
    </div>
  )
})
